/**
 * The lease behind the simulator panel.
 *
 * This is the only stateful piece of the simulator surface, and it is
 * deliberately thin: every decision it could get wrong already lives somewhere
 * pure and tested. `leaseToPanelState` decides what the user is told,
 * `chooseSimHost` decides where the lease goes, and `shouldKeepPolling` decides
 * when to stop asking. What is left here is the plumbing — fetch, interval,
 * cleanup — which is the part a test would mostly be re-describing.
 *
 * Two rules the plumbing does enforce:
 *
 *   - **Nothing is claimed without consent.** Allocation costs a real machine,
 *     so the first render asks rather than acts.
 *   - **A held lease is renewed, or it dies.** Renewal is the sole proof of
 *     ownership (design doc E8-1); a lease nobody renews is reaped, and to the
 *     user that looks like the simulator failing on its own.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { cmmdApiClient, type CmmdSimHost, type CmmdSimLease } from "../../environments/cmmdApi";
import { chooseSimHost, queueDepth, shouldClaimNow } from "./chooseSimHost";
import { leaseToPanelState, shouldKeepPolling, type LeaseState } from "./leaseToPanelState";
import type { SimulatorPanelState } from "./simulatorPanelState";

/** Long enough to survive a slow poll, short enough that an abandoned tab frees the host. */
const LEASE_TTL_MS = 5 * 60 * 1000;
/** Renew at a third of the TTL, so two missed renewals still do not expire the lease. */
const RENEW_INTERVAL_MS = LEASE_TTL_MS / 3;
const POLL_INTERVAL_MS = 3_000;

export interface UseSimulatorLeaseInput {
  readonly forgeEnvironmentId: string;
  readonly threadId: string;
}

export interface SimulatorLeaseView {
  readonly state: SimulatorPanelState;
  readonly onConsent: () => void;
  readonly onRelease: () => void;
  readonly onRetry: () => void;
}

export function useSimulatorLease(input: UseSimulatorLeaseInput): SimulatorLeaseView {
  const [consentGiven, setConsentGiven] = useState(false);
  const [hosts, setHosts] = useState<ReadonlyArray<CmmdSimHost> | null>(null);
  const [lease, setLease] = useState<CmmdSimLease | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Intervals read through refs so the timers do not have to be torn down and
  // rebuilt on every update. That is not just an optimisation: an interval that
  // closes over a render's value evaluates a FROZEN one for its whole life, so
  // a guard written against it reads correct and never fires.
  const leaseRef = useRef<CmmdSimLease | null>(null);
  leaseRef.current = lease;
  const stateRef = useRef<SimulatorPanelState>("checking-setup");
  // One claim at a time. The claim effect re-runs on every poll, so without this
  // a consented-but-queued panel would fire a claim per tick at the same host.
  const claimInFlight = useRef(false);

  const scope = { forgeEnvironmentId: input.forgeEnvironmentId, threadId: input.threadId };

  const state = leaseToPanelState({
    leaseState: (lease?.state ?? null) as LeaseState | null,
    readiness: {
      // Null hosts means the list has not come back yet, which is
      // `checking-setup`. An EMPTY list is a different answer: the workspace has
      // no Mac host, and the panel must say so rather than spin.
      reported: hosts !== null,
      hasIosRuntime: (hosts?.length ?? 0) > 0,
    },
    lastSignal: null,
    consentGiven,
    queuedBehind: queueDepth(hosts ?? []),
    // No stream yet, so no frame has ever arrived. `leaseToPanelState` maps an
    // active lease with no frames to `booting`, which is the honest answer —
    // claiming `live` here would put a live dot over a blank device.
    lastFrameAtMs: null,
    nowMs,
    retrySeconds: null,
  });

  // Assigned during render, so the interval below always sees the state from
  // the most recent one rather than the state at the time it was created.
  stateRef.current = state;

  /** One poll: hosts and the current lease, together. */
  const refresh = useCallback(async () => {
    const [nextHosts, nextLease] = await Promise.all([
      cmmdApiClient.listSimHosts(),
      cmmdApiClient.getSimLease(scope),
    ]);
    setHosts(nextHosts);
    setLease(nextLease);
    setNowMs(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scope is derived from the two ids below
  }, [input.forgeEnvironmentId, input.threadId]);

  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      void refresh();
    };
    tick();
    const id = window.setInterval(() => {
      // Terminal states stop, so a blocked lease does not spin a request loop
      // against a host that will never answer differently. Read from the ref:
      // `state` here would be the value from the render that created this
      // interval, which is always `checking-setup` — a state that keeps
      // polling, so the guard would be dead code.
      if (shouldKeepPolling(stateRef.current)) tick();
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [refresh]);

  useEffect(() => {
    const id = window.setInterval(() => {
      const held = leaseRef.current;
      if (held === null || held.state === "blocked" || held.state === "released") return;
      void cmmdApiClient
        .renewSimLease({
          leaseId: held.id,
          generation: held.generation,
          rowVersion: held.rowVersion,
          ttlMs: LEASE_TTL_MS,
        })
        .then(setLease)
        // A refused renewal means the fence moved under us — the next poll reads
        // the real row. Retrying with the stale fence would only be refused again.
        .catch(() => undefined);
    }, RENEW_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  /**
   * Claim whenever the conditions allow it, not only at the moment of consent.
   *
   * Consent is not a claim. A user can agree while every host is full, and the
   * lease then has to be taken when one frees up — polling only reads state, so
   * without this the panel sits in `queued` forever.
   */
  useEffect(() => {
    const host = chooseSimHost(hosts ?? []);
    const allowed = shouldClaimNow({
      consentGiven,
      leaseState: (lease?.state ?? null) as LeaseState | null,
      hasFreeHost: host !== null,
    });
    if (!allowed || host === null || claimInFlight.current) return;

    claimInFlight.current = true;
    void cmmdApiClient
      .claimSimLease({
        forgeEnvironmentId: input.forgeEnvironmentId,
        threadId: input.threadId,
        hostId: host.id,
        ttlMs: LEASE_TTL_MS,
      })
      .then(setLease)
      // A lost race is ordinary — another claimer took the slot. The next poll
      // re-reads the fleet and this effect decides again.
      .catch(() => undefined)
      .finally(() => {
        claimInFlight.current = false;
      });
  }, [consentGiven, hosts, lease, input.forgeEnvironmentId, input.threadId]);

  const onConsent = useCallback(() => {
    setConsentGiven(true);
  }, []);

  const onRelease = useCallback(() => {
    const held = leaseRef.current;
    setConsentGiven(false);
    if (held === null) return;
    void cmmdApiClient
      .releaseSimLease({
        leaseId: held.id,
        generation: held.generation,
        rowVersion: held.rowVersion,
      })
      .then(() => void refresh())
      .catch(() => undefined);
  }, [refresh]);

  const onRetry = useCallback(() => {
    void refresh();
  }, [refresh]);

  return { state, onConsent, onRelease, onRetry };
}
